import React, { useMemo, useCallback, useRef } from 'react';
import type { TrackProps, PageProps, IdentifyProp, ActionType, SegmentGroupProps } from './context';
import { AnalyticsContext } from './context';
import type { SegmentService } from './segment/service';
import { getLocalStorage } from '../storage';
import type { Logger } from '../logging';
import { getActionTypeLogger } from '../logging';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { getAnalyticsId } from './segment/getAnalyticsId';

enum MEDIA {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

const getActionType = (actionType?: ActionType) => {
  switch (actionType) {
    case 'click':
      return 'Clicked';
    case 'select':
      return 'Selected';
    case 'error':
      return 'Error';
    default:
      return '';
  }
};

const pageFunc =
  (service: SegmentService, analyticsId: string, logger?: Logger, viewportSize?: Maybe<MEDIA>) =>
  ({ category, page, pagePrefix, action, time, extraPageInfo, referrerOverride, context }: PageProps) => {
    const localStorage = getLocalStorage();
    const joyPlatform = localStorage.getItem('segmentPlatform');
    const joyPlatformData = localStorage.getItem('segmentPlatformData');
    let timeSpentInSeconds;
    if (time) {
      timeSpentInSeconds = (time.endTime - time.startTime) / 1000;
    }
    if (logger) {
      logger.info({
        analyticsId,
        page,
        action,
        category,
        pagePrefix,
        timeSpentInSeconds,
        extraPageInfo: {
          ...extraPageInfo,
          joyPlatformData,
          joyPlatform,
          viewportSize,
        },
        referrerOverride,
        context,
      });
    }
    service.sendPage({
      analyticsId,
      page,
      action,
      category,
      pagePrefix,
      timeSpentInSeconds,
      extraPageInfo: {
        ...extraPageInfo,
        joyPlatformData,
        joyPlatform,
        viewportSize,
      },
      referrerOverride,
      context,
    });
  };

const trackFunc = (service: SegmentService, analyticsId: string, logger?: Logger, viewportSize?: Maybe<MEDIA>) => (props: TrackProps) => {
  const localStorage = getLocalStorage();
  const joyPlatform = localStorage.getItem('segmentPlatform');
  const joyPlatformData = localStorage.getItem('segmentPlatformData');
  const { category, page, pagePrefix, action, actionType, label, extraInfo, referrerOverride, context, callback } = props;
  const actionPlusType = action + getActionType(actionType);

  if (logger) {
    const handler = getActionTypeLogger(logger, actionType);
    handler({
      analyticsId,
      category,
      page,
      pagePrefix,
      action: actionPlusType,
      actionType,
      label,
      extraInfo: {
        ...extraInfo,
        joyPlatform,
        joyPlatformData,
        viewportSize,
      },
      referrerOverride,
      context,
    });
  }

  service.sendEvent({
    analyticsId,
    category,
    page,
    pagePrefix,
    action: actionPlusType,
    actionType,
    label,
    extraInfo: {
      ...extraInfo,
      joyPlatform,
      joyPlatformData,
      viewportSize,
    },
    referrerOverride,
    context,
    callback,
  });
};

const identifyFunc = (service: SegmentService, analyticsId: string) => (args: IdentifyProp) => {
  service.identify({ ...args, analyticsId });
};

export const AnalyticsProvider: React.FC<{ segment: SegmentService; logger?: Logger }> = ({ segment, logger, children }) => {
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } }, false);
  // Read at call time through a ref: the viewport class only matters for the event being sent, and
  // baking it into `page` / `track` made every analytics consumer re-render on each breakpoint flip.
  const viewportSizeRef = useRef<MEDIA>(isMobile ? MEDIA.Mobile : MEDIA.Desktop);
  viewportSizeRef.current = isMobile ? MEDIA.Mobile : MEDIA.Desktop;
  const analyticsId = getAnalyticsId();
  const page = useCallback((props: PageProps) => pageFunc(segment, analyticsId, logger, viewportSizeRef.current)(props), [segment, analyticsId, logger]);
  const track = useCallback((props: TrackProps) => trackFunc(segment, analyticsId, logger, viewportSizeRef.current)(props), [segment, analyticsId, logger]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const identify = useCallback(identifyFunc(segment, analyticsId), [segment, analyticsId]);

  const group = useCallback(
    (args: SegmentGroupProps) => {
      segment.group(args);
    },
    [segment],
  );

  const anonymousId = useMemo<string>(() => segment.anonymousId(), [segment]);

  const contextValue = useMemo(() => {
    return {
      track,
      page,
      identify,
      group,
      anonymousId,
    };
  }, [track, page, identify, group, anonymousId]);

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};
